import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Check } from 'lucide-react';
import { apiClient, ApiError, UserNotification } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useVisiblePolling } from '../hooks/useVisiblePolling';

interface NotificationBellProps {
    className?: string;
    onConsentResultsClick?: (data: any) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '', onConsentResultsClick }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<UserNotification[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [markingId, setMarkingId] = useState<string | null>(null);
    const [refreshError, setRefreshError] = useState<Error | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        try {
            const result = await apiClient.getUnreadNotificationCount();
            setUnreadCount(result.unreadCount);
            setRefreshError(null);
        } catch (error: unknown) {
            const requestError = error instanceof Error ? error : new Error('Failed to refresh notifications');
            setRefreshError(requestError);
            console.error('Error fetching unread count:', requestError);
            throw requestError;
        }
    }, []);

    // Fetch notifications for dropdown
    const fetchNotifications = async () => {
        try {
            setIsLoading(true);
            const result = await apiClient.getUserNotifications({ limit: 10 });
            setNotifications(result.notifications);
            setUnreadCount(result.unreadCount);
            setRefreshError(null);
        } catch (error: unknown) {
            const requestError = error instanceof Error ? error : new Error('Failed to refresh notifications');
            setRefreshError(requestError);
            console.error('Error fetching notifications:', requestError);
        } finally {
            setIsLoading(false);
        }
    };

    useVisiblePolling(fetchUnreadCount, { intervalMs: 60000 });

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (isDropdownOpen) {
            fetchNotifications();
        }
    }, [isDropdownOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleBellClick = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleNotificationClick = async (notification: UserNotification) => {
        try {
            // Mark as read
            if (!notification.isRead) {
                await apiClient.markNotificationAsRead(notification.id);
                await fetchNotifications(); // Refresh list
            }

            // Handle bulk_consent_sent differently
            if (notification.type === 'bulk_consent_sent') {
                setIsDropdownOpen(false);
                // Trigger custom modal with notification data
                if (onConsentResultsClick) {
                    onConsentResultsClick(notification.data);
                }
                return;
            }

            // Navigate to action URL if provided
            if (notification.actionUrl) {
                setIsDropdownOpen(false);

                // For automated check alerts, pass the bulk operation ID
                if (notification.type === 'automated_check_alert' && notification.data?.bulkOperationId) {
                    navigate(`${notification.actionUrl}?bulkOperationId=${notification.data.bulkOperationId}`);
                } else {
                    navigate(notification.actionUrl);
                }
            }
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    const handleMarkOneAsRead = async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation();
        if (markingId) return;
        try {
            setMarkingId(notificationId);
            await apiClient.markNotificationAsRead(notificationId);
            await fetchNotifications();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        } finally {
            setMarkingId(null);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await apiClient.markAllNotificationsAsRead();
            await fetchNotifications(); // Refresh list
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'report_ready':
                return '📊';
            case 'report_scheduled':
                return '⏰';
            case 'report_failed':
                return '⚠️';
            case 'system':
                return 'ℹ️';
            case 'automated_check_alert':
                return '🤖';
            case 'bulk_consent_sent':
                return '✉️';
            default:
                return '📬';
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={handleBellClick}
                className="relative flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[80vh] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllAsRead}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                            >
                                <CheckCheck className="w-4 h-4" />
                                <span>Mark all read</span>
                            </button>
                        )}
                    </div>

                    {refreshError && (
                        <div className={`border-b px-4 py-2 text-xs ${refreshError instanceof ApiError && refreshError.status === 429
                            ? 'border-amber-200 bg-amber-50 text-amber-800'
                            : 'border-red-200 bg-red-50 text-red-700'
                            }`}>
                            {refreshError instanceof ApiError && refreshError.status === 429
                                ? `Updates paused.${refreshError.retryAfterSeconds ? ` Retrying in ${refreshError.retryAfterSeconds} seconds.` : ' Retrying automatically.'}`
                                : "We couldn't refresh notifications."}
                        </div>
                    )}

                    {/* Notifications List */}
                    <div className="overflow-y-auto flex-1">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin h-6 w-6 border-2 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>No notifications</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${!notification.isRead ? 'bg-blue-50' : ''
                                            }`}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <span className="text-2xl flex-shrink-0">
                                                {getNotificationIcon(notification.type)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between">
                                                    <p className={`text-sm font-medium text-gray-900 ${!notification.isRead ? 'font-semibold' : ''}`}>
                                                        {notification.title}
                                                    </p>
                                                    {!notification.isRead && (
                                                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                                            <div className="relative group">
                                                                <button
                                                                    onClick={(e) => handleMarkOneAsRead(e, notification.id)}
                                                                    disabled={markingId === notification.id}
                                                                    className="flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                                                                    aria-label="Mark as read"
                                                                >
                                                                    <Check className="w-3.5 h-3.5" />
                                                                </button>
                                                                <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-1.5 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    Mark as read
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formatTimeAgo(notification.createdAt)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200 text-center">
                            <button
                                onClick={() => {
                                    setIsDropdownOpen(false);
                                    navigate('/reports?tab=history');
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                View all reports
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
